import React from "react";
import { EmployeeContractDetail } from "../../../types/admin";

interface Props {
  contract: EmployeeContractDetail;
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return "        년     월     일";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "        년     월     일";
  return `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(2, '0')}월 ${String(date.getDate()).padStart(2, '0')}일`;
};

const formatAmount = (amount?: number | string) => {
  if (amount == null) return "           ";
  return Number(amount).toLocaleString();
};

const renderSignature = (signatureText: string | undefined, name: string | undefined) => {
  if (signatureText && signatureText.startsWith("data:image")) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span>{name || "__________"} (서명:</span>
        <img 
          src={signatureText} 
          alt="서명" 
          style={{ 
            height: "40px", 
            objectFit: "contain", 
            borderBottom: "1px solid #1d1d1f",
            paddingBottom: "2px"
          }} 
        />
        <span>)</span>
      </div>
    );
  }
  return <span>{name || "__________"} (서명: {signatureText || "          "})</span>;
};

const GuardianConsentViewer: React.FC<{ form: any; contract: EmployeeContractDetail }> = ({ form, contract }) => {
  return (
    <div style={{
      maxWidth: '700px', margin: '0 auto', backgroundColor: '#ffffff',
      padding: '48px 40px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      fontFamily: "'Pretendard', sans-serif", color: '#1d1d1f', lineHeight: '1.6', fontSize: '14px',
    }}>
      <h1 style={{ textAlign: 'center', fontSize: '24px', fontWeight: '700', marginBottom: '40px' }}>
        친권자(후견인) 동의서
      </h1>

      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>친권자(후견인) 인적사항</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 16px', paddingLeft: '16px' }}>
          <div style={{ color: '#666874' }}>성 명 :</div>
          <div>{form.guardian_name || "____________________"}</div>
          <div style={{ color: '#666874' }}>주민등록번호 :</div>
          <div>{form.guardian_resident_id_masked || form.guardian_resident_id || "____________________"}</div>
          <div style={{ color: '#666874' }}>주 소 :</div>
          <div>{form.guardian_address || "____________________________________"}</div>
          <div style={{ color: '#666874' }}>연 락 처 :</div>
          <div>{form.guardian_phone_number || form.guardian_phone || "____________________"}</div>
          <div style={{ color: '#666874' }}>연소근로자와의 관계 :</div>
          <div>{form.relation_to_minor_worker || form.guardian_relation || "____________________"}</div>
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>연소근로자 인적사항</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 16px', paddingLeft: '16px' }}>
          <div style={{ color: '#666874' }}>성 명 :</div>
          <div>{form.minor_name || form.worker_name || "____________________"} {form.minor_age || form.worker_age ? `(만 ${form.minor_age || form.worker_age}세)` : "(만          세)"}</div>
          <div style={{ color: '#666874' }}>주민등록번호 :</div>
          <div>{form.minor_resident_id_masked || form.worker_resident_id || "____________________"}</div>
          <div style={{ color: '#666874' }}>주 소 :</div>
          <div>{form.minor_address || form.worker_address || "____________________________________"}</div>
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>사업장 개요</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 16px', paddingLeft: '16px' }}>
          <div style={{ color: '#666874' }}>회 사 명 :</div>
          <div>{form.business_name || form.employer_business_name || "____________________"}</div>
          <div style={{ color: '#666874' }}>회 사 주 소 :</div>
          <div>{form.business_address || form.employer_address || "____________________________________"}</div>
          <div style={{ color: '#666874' }}>대 표 자 :</div>
          <div>{form.business_representative_name || form.employer_representative_name || "____________________"}</div>
          <div style={{ color: '#666874' }}>회 사 전 화 :</div>
          <div>{form.business_phone_number || form.employer_phone || "____________________"}</div>
        </div>
      </div>

      <div style={{ marginTop: '32px', marginBottom: '40px', textAlign: 'center', fontSize: '15px' }}>
        본인은 위 연소근로자 <strong>{form.consent_minor_name || form.minor_name || form.worker_name || "__________"}</strong> 가(이) 위 사업장에서 근로를 하는 것에 대하여 동의합니다.
      </div>

      <div style={{ marginTop: '40px', textAlign: 'center', fontSize: '16px', marginBottom: '40px' }}>
        {formatDate(form.consent_signed_date || form.contract_signed_date || contract.created_at)}
      </div>

      <div style={{ textAlign: 'right', fontSize: '15px', marginBottom: '40px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <span style={{ marginRight: '8px' }}>친권자(후견인) :</span>
        {renderSignature(form.guardian_signature_text || form.guardian_signature_name, form.guardian_name)}
      </div>

      <div style={{ fontSize: '14px', color: '#666874' }}>
        첨 부 : 가족관계증명서 1부 {form.family_relation_certificate_attached ? `(제출: ${form.family_relation_certificate_attached})` : ""}
      </div>
    </div>
  );
};

const StandardContractViewer: React.FC<{ form: any; contract: EmployeeContractDetail }> = ({ form, contract }) => {
  const isMinor = contract.template_version.includes("minor") || contract.template_version.includes("youth");

  // Wage Type Format
  const wageTypeMap: Record<string, string> = {
    monthly: "월급",
    daily: "일급",
    hourly: "시급"
  };
  const wageTypeLabel = wageTypeMap[form.wage_type] || "임금";

  const daysOfWeek = ["월", "화", "수", "목", "금", "토", "일"];
  const workingDays = [];
  for (let i = 0; i < 7; i++) {
    if (form[`work_day_${i}_enabled`] === "1" || form[`work_day_${i}_enabled`] === true) {
      workingDays.push(daysOfWeek[i]);
    }
  }

  return (
    <div style={{
      maxWidth: '700px', margin: '0 auto', backgroundColor: '#ffffff',
      padding: '48px 40px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      fontFamily: "'Pretendard', sans-serif", color: '#1d1d1f', lineHeight: '1.6', fontSize: '14px',
    }}>
      <h1 style={{ textAlign: 'center', fontSize: '24px', fontWeight: '700', marginBottom: '40px' }}>
        {isMinor ? "연소근로자 표준근로계약서" : "표준 근로계약서"}
      </h1>

      <p style={{ marginBottom: '24px' }}>
        <strong>{form.employer_representative_name || form.employer_name || "________"}</strong>(이하 "사업주"라 함)과(와) <strong>{form.worker_name || "________"}</strong>(이하 "근로자"라 함)은 다음과 같이 근로계약을 체결한다.
      </p>

      <ol style={{ paddingLeft: '20px', margin: 0, listStyleType: 'decimal' }}>
        <li style={{ marginBottom: '12px' }}>
          <span style={{ fontWeight: '600' }}>근로계약기간</span> : {formatDate(form.contract_start_date)} 부터 {form.contract_end_date ? formatDate(form.contract_end_date) : "        년     월     일"}까지
          {(!form.contract_end_date) && <div style={{ color: '#666874', fontSize: '13px', marginTop: '4px' }}>※ 근로계약기간을 정하지 않는 경우에는 "근로개시일"만 기재</div>}
        </li>

        <li style={{ marginBottom: '12px' }}>
          <span style={{ fontWeight: '600' }}>근 무 장 소</span> : {form.work_place || "____________________________________________"}
        </li>

        <li style={{ marginBottom: '12px' }}>
          <span style={{ fontWeight: '600' }}>업 무 내 용</span> : {form.job_description || "____________________________________________"}
        </li>

        <li style={{ marginBottom: '12px' }}>
          <span style={{ fontWeight: '600' }}>소정근로시간</span> : {form.scheduled_work_start_time || "    :    "} 부터 {form.scheduled_work_end_time || "    :    "} 까지
          <br />
          (휴게시간: {form.break_start_time || "    :    "} ~ {form.break_end_time || "    :    "})
        </li>

        <li style={{ marginBottom: '12px' }}>
          <span style={{ fontWeight: '600' }}>근무일/휴일</span> : 매주 <strong>{form.work_days_per_week || workingDays.length || "   "}</strong>일({workingDays.length > 0 ? workingDays.join(", ") : "또는 매일단위"})근무, 주휴일 매주 <strong>{form.weekly_holiday_day || "   "}</strong>요일
        </li>

        <li style={{ marginBottom: '12px' }}>
          <span style={{ fontWeight: '600' }}>임 금</span>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '8px' }}>
            <li>{wageTypeLabel} : {formatAmount(form.wage_amount)} 원</li>
            <li>상여금 : {form.bonus_included ? "있음(O)" : "있음( )"}, 없음({form.bonus_included ? " " : "O"}) {form.bonus_included ? `${formatAmount(form.bonus_amount)}원` : ""}</li>
            <li>기타급여(제수당 등) : {form.other_allowance_included ? "있음(O)" : "있음( )"}, 없음({form.other_allowance_included ? " " : "O"})
              {form.other_allowance_included && (
                <ul style={{ listStyleType: 'circle', paddingLeft: '20px', marginTop: '4px' }}>
                  {form.meal_allowance > 0 && <li>식대(비과세): {formatAmount(form.meal_allowance)}원</li>}
                  {form.transport_allowance > 0 && <li>교통비: {formatAmount(form.transport_allowance)}원</li>}
                  {form.extra_allowance_amount > 0 && <li>{form.extra_allowance_name || "기타수당"}: {formatAmount(form.extra_allowance_amount)}원</li>}
                  {form.other_allowance_amount > 0 && <li>기타수당: {formatAmount(form.other_allowance_amount)}원</li>}
                </ul>
              )}
            </li>
            <li>임금지급일 : 매월(매주 또는 매일) <strong>{form.payment_day || "   "}</strong>일 (휴일의 경우는 전일 지급)</li>
            <li>지급방법 : 근로자에게 직접지급({form.payment_method === "direct" ? "O" : " "}), 근로자 명의 예금통장에 입금({form.payment_method === "bank_transfer" ? "O" : " "})</li>
          </ul>
        </li>

        <li style={{ marginBottom: '12px' }}>
          <span style={{ fontWeight: '600' }}>연차유급휴가</span>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '8px' }}>
            <li>{form.annual_leave_note || "연차유급휴가는 근로기준법에서 정하는 바에 따라 부여함"}</li>
          </ul>
        </li>

        {isMinor && (
          <li style={{ marginBottom: '12px' }}>
            <span style={{ fontWeight: '600' }}>가족관계증명서 및 동의서</span>
            <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '8px' }}>
              <li>가족관계기록사항에 관한 증명서 제출 여부 : {typeof form.family_relation_certificate_submitted === 'string' ? form.family_relation_certificate_submitted : (form.family_cert_submitted ? "예" : "아니오")}</li>
              <li>친권자 또는 후견인의 동의서 구비 여부 : {typeof form.guardian_consent_submitted === 'string' ? form.guardian_consent_submitted : (form.guardian_consent_submitted === true ? "예" : "아니오")}</li>
            </ul>
          </li>
        )}

        <li style={{ marginBottom: '12px' }}>
          <span style={{ fontWeight: '600' }}>근로계약서 교부</span>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '8px' }}>
            <li>사업주는 근로계약을 체결함과 동시에 본 계약서를 사본하여 근로자의 교부요구와 관계없이 근로자에게 교부함(근로기준법 제17조{isMinor ? ", 제67조" : ""} 이행)</li>
          </ul>
        </li>

        <li style={{ marginBottom: '12px' }}>
          <span style={{ fontWeight: '600' }}>기 타</span>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '8px' }}>
            {isMinor && <li>13세 이상 15세 미만인 자에 대해서는 고용노동부장관으로부터 취직인허증을 교부받아야 하며, 이 계약에 정함이 없는 사항은 근로기준법령에 의함</li>}
            {!isMinor && <li>이 계약에 정함이 없는 사항은 근로기준법령에 의함</li>}
          </ul>
        </li>
      </ol>

      <div style={{ marginTop: '40px', textAlign: 'center', fontSize: '16px' }}>
        {formatDate(form.contract_signed_date || contract.created_at)}
      </div>

      <div style={{ marginTop: '40px', borderTop: '1px solid #e7e8ef', paddingTop: '24px' }}>
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontWeight: '600', marginBottom: '8px' }}>(사업주)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '8px 16px' }}>
            <div style={{ color: '#666874' }}>사업체명 :</div>
            <div>{form.employer_business_name || "_________________________"} (전화: {form.employer_phone || "____________"})</div>
            
            <div style={{ color: '#666874' }}>주 소 :</div>
            <div>{form.employer_address || "____________________________________"}</div>
            
            <div style={{ color: '#666874', display: 'flex', alignItems: 'center' }}>대 표 자 :</div>
            <div>{renderSignature(form.employer_signature_text, form.employer_representative_name || form.employer_name)}</div>
          </div>
        </div>

        <div>
          <div style={{ fontWeight: '600', marginBottom: '8px' }}>(근로자)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '8px 16px' }}>
            <div style={{ color: '#666874' }}>주 소 :</div>
            <div>{form.worker_address || "____________________________________"}</div>
            
            <div style={{ color: '#666874' }}>연 락 처 :</div>
            <div>{form.worker_phone || "__________"}</div>
            
            <div style={{ color: '#666874', display: 'flex', alignItems: 'center' }}>성 명 :</div>
            <div>{renderSignature(form.worker_signature_text, form.worker_name)}</div>
          </div>
        </div>
      </div>
      
    </div>
  );
};


const ContractViewer: React.FC<Props> = ({ contract }) => {
  const form = contract.form_values || {};
  const isGuardianConsent = contract.template_version && contract.template_version.includes("guardian");

  return (
    <div style={{
      width: '100%',
      height: '100%',
      backgroundColor: '#f5f5f7',
      overflowY: 'auto',
      padding: '24px',
      boxSizing: 'border-box',
    }}>
      <div id="pdf-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {isGuardianConsent ? (
          <GuardianConsentViewer form={form} contract={contract} />
        ) : (
          <StandardContractViewer form={form} contract={contract} />
        )}
      </div>
    </div>
  );
};

export default ContractViewer;
